import { Email as EmailIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

// Pedagogisk bild av e-postmeddelandet, driven av rubrik och text medan de skrivs. Används i
// utskicksbeställningen och vid redigering av en fakturamall, så att förhandsvisningen ser
// likadan ut oavsett var texterna skrivs.
interface SendingEmailMockProps {
  senderName?: string;
  heading?: string;
  // Brödtexten som HTML, alltså det editorn producerar.
  html?: string;
  // Logotyp att visa. Saknas den ritas en platshållare.
  logoSrc?: string;
  // Upplysning under loggan, till exempel att den byts per medlem vid utskicket.
  logoNote?: string;
  attachmentName?: string;
  ctaLabel?: string;
}

export const SendingEmailMock = ({
  senderName,
  heading,
  html,
  logoSrc,
  logoNote,
  attachmentName = 'Faktura.pdf',
  ctaLabel = 'Gå till fakturan och betala här',
}: SendingEmailMockProps) => (
  // zoom krymper hela mejlkortet (både layout och innehåll) så previewen blir kompakt även för stora mejltexter/bilder.
  <Box
    sx={{
      border: 1,
      borderColor: 'grey.300',
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: '#fff',
      boxShadow: 3,
      maxWidth: 400,
      mx: 'auto',
      zoom: 0.8,
    }}
  >
    <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5, borderBottom: 1, borderColor: 'grey.300' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <EmailIcon fontSize='small' color='action' />
        <Typography variant='caption' fontWeight={600} color='text.secondary'>
          Från: {senderName || ''}
        </Typography>
      </Box>
      <Typography variant='body2' fontWeight={600}>
        {heading || (
          <Box component='span' sx={{ color: 'text.disabled' }}>
            Ingen rubrik angiven
          </Box>
        )}
      </Typography>
      {/* Bilaga visas högst upp (nära ämne/avsändare), som i en e-postklient. */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 1,
          px: 1,
          py: 0.25,
          bgcolor: '#fff',
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1,
        }}
      >
        <PictureAsPdfIcon fontSize='small' color='error' />
        <Typography variant='caption' color='text.secondary'>
          {attachmentName}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ px: 2, py: 2 }}>
      {/* Logotyp överst i mejlet (som i det skarpa utskicket). Saknas logga visas placeholder. */}
      <Box sx={{ mb: 1.5 }}>
        {logoSrc ? (
          <Box component='img' src={logoSrc} alt='Logotyp' sx={{ maxHeight: 32, maxWidth: 150 }} />
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 90,
              height: 30,
              border: '1px dashed',
              borderColor: 'grey.400',
              borderRadius: 0.5,
            }}
          >
            <Typography variant='caption' color='text.disabled'>
              LOGO
            </Typography>
          </Box>
        )}
        {logoNote && (
          <Typography variant='caption' color='primary.main' sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            {logoNote}
          </Typography>
        )}
      </Box>
      {html ? (
        // eslint-disable-next-line react/no-danger
        <Typography
          variant='body2'
          color='text.secondary'
          component='div'
          // Innesluter kundens egen CSS (t.ex. vattenmärken med position: fixed) i rutan.
          sx={{ contain: 'paint', position: 'relative', overflow: 'hidden' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <Typography variant='body2' color='text.disabled'>
          Ingen text angiven
        </Typography>
      )}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'grey.700',
          color: '#fff',
          px: 2,
          py: 0.75,
          borderRadius: 1,
          fontSize: '0.8rem',
          fontWeight: 600,
          mt: 2,
        }}
      >
        {ctaLabel}
      </Box>
    </Box>
  </Box>
);
